import { formatFee, formatUsd } from "./engine";
import type { RunResult } from "./types";

export interface Insight {
  icon: string;
  /** Short headline. */
  title: string;
  /** One-line explanation of *why* the result came out this way. */
  detail: string;
  /** Which coin this favors, for styling. */
  favors: "BCH" | "BTC" | "neutral";
}

/**
 * Produce a short list of plain-language takeaways comparing the two runs.
 * The goal is to TEACH: explain exactly where the difference came from,
 * honestly, using the player's own numbers.
 */
export function buildInsights(bch: RunResult, btc: RunResult): Insight[] {
  const insights: Insight[] = [];

  // 1. Fees — the headline.
  const feeDiff = round2(btc.totalFeesUsd - bch.totalFeesUsd);
  if (feeDiff > 0.5) {
    const spikePart =
      btc.spikeFeesUsd > 0
        ? ` ${formatUsd(btc.spikeFeesUsd)} of that hit on congested days alone.`
        : "";
    insights.push({
      icon: "🧾",
      title: `You paid ${formatUsd(feeDiff)} more in fees with BTC`,
      detail: `BTC fees totaled ${formatFee(btc.totalFeesUsd)} vs just ${formatFee(bch.totalFeesUsd)} in BCH.${spikePart} BCH's fee never moved.`,
      favors: "BCH",
    });
  } else {
    insights.push({
      icon: "🧾",
      title: "Fees were close this week",
      detail: `You mostly used cheap payments (Lightning, small buys), so on-chain fees barely differed: ${formatFee(bch.totalFeesUsd)} BCH vs ${formatFee(btc.totalFeesUsd)} BTC.`,
      favors: "neutral",
    });
  }

  // 2. Congestion spikes specifically.
  if (btc.spikeFeesUsd > 0) {
    insights.push({
      icon: "⚠️",
      title: "BTC congestion bit you",
      detail: `On busy days, your BTC on-chain payments cost ${formatUsd(btc.spikeFeesUsd)} in spiked fees. The same payments in BCH cost a fraction of a cent — big blocks mean no fee market.`,
      favors: "BCH",
    });
  }

  // 2b. Getting paid — receive fees eat into crypto payouts too.
  const earnDiff = round2(bch.totalEarnedUsd - btc.totalEarnedUsd);
  if (bch.totalEarnedUsd > 0 && earnDiff > 0.5) {
    insights.push({
      icon: "💰",
      title: `You kept ${formatUsd(earnDiff)} more of what you earned in BCH`,
      detail: `Getting paid isn't free either — a payer's on-chain fee comes out of what lands in your wallet. You netted ${formatUsd(bch.totalEarnedUsd)} in BCH vs ${formatUsd(btc.totalEarnedUsd)} in BTC on the same income, since BCH's receive fee barely registers.`,
      favors: "BCH",
    });
  }

  // 3. Volatility — cuts honestly both ways.
  // Isolate PRICE drift by backing out money that flowed in/out of the crypto
  // wallet: add back what was spent from crypto, subtract what was earned into it.
  const bchGrowth = round2(
    bch.remainingCryptoUsd + spentFromCrypto(bch) - earnedIntoCrypto(bch) - bch.startingCryptoUsd,
  );
  const btcGrowth = round2(
    btc.remainingCryptoUsd + spentFromCrypto(btc) - earnedIntoCrypto(btc) - btc.startingCryptoUsd,
  );
  if (Math.abs(btcGrowth - bchGrowth) > 3) {
    const btcMoved = btcGrowth >= bchGrowth;
    insights.push({
      icon: btcMoved ? "📈" : "📉",
      title: btcMoved
        ? "BTC's price moved more in your favor"
        : "BCH held up better on price",
      detail: btcMoved
        ? "Volatility cuts both ways — this week BTC's price swing helped its remaining balance. Price gains aren't a payment feature, but they're real."
        : "This week BCH's price drifted more favorably, padding your remaining balance. Volatility is a wash over time.",
      favors: btcMoved ? "BTC" : "BCH",
    });
  }

  // 4. Final score summary.
  const scoreDiff = bch.score - btc.score;
  if (scoreDiff > 0) {
    insights.push({
      icon: "🏆",
      title: `BCH scored ${scoreDiff} point${scoreDiff === 1 ? "" : "s"} higher`,
      detail:
        "For everyday spending — lots of small, frequent, on-chain payments — flat near-zero fees add up in BCH's favor.",
      favors: "BCH",
    });
  } else if (scoreDiff < 0) {
    insights.push({
      icon: "🤔",
      title: `BTC edged ahead by ${-scoreDiff}`,
      detail:
        "This run favored BTC — often because you leaned on Lightning or hit a calm-fee week. Try again on a congested week to see the difference.",
      favors: "BTC",
    });
  } else {
    insights.push({
      icon: "🤝",
      title: "It was a tie this run",
      detail: "Play again — a congested BTC week tends to tilt things toward BCH's flat fees.",
      favors: "neutral",
    });
  }

  return insights;
}

/** Total USD drawn OUT of the crypto balance (cost + fee) across the run. */
function spentFromCrypto(run: RunResult): number {
  return run.decisions
    .filter((d) => d.flow === "spend" && d.method === "crypto")
    .reduce((sum, d) => sum + d.costUsd + d.feeUsd, 0);
}

/** Total NET USD received INTO the crypto balance across the run. */
function earnedIntoCrypto(run: RunResult): number {
  return run.decisions
    .filter((d) => d.flow === "earn" && d.method === "crypto")
    .reduce((sum, d) => sum + d.earnedUsd, 0);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
